/**
 * Bonus Suitcase Scene
 *
 * 3D money suitcase that the player interacts with to claim daily bonus:
 *   1. Tap left lock  → lock flips open
 *   2. Tap right lock → lock flips open
 *   3. Tap lid/handle → lid swings open, prize revealed
 *
 * Uses SharedRenderer singleton — no extra WebGL context.
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SharedRenderer } from '../../engine/render/SharedRenderer';
import { EventEmitter } from '../../engine/core/EventEmitter';
import { sound, SoundId } from '../../engine/audio/sound';

const SUITCASE_GLB = '/assets/models/money-suitcase.glb';

/** Prize at/above this (Stacks) plays the high-tier reveal stinger. */
const BIG_PRIZE_THRESHOLD = 20_000_000;
/** Delay after the lid pops before the prize reveal stinger lands. */
const PRIZE_SFX_DELAY_MS = 150;

// Preload bill image once at module level — always ready when scene is created
const BILL_IMG = new Image();
BILL_IMG.src = '/assets/images/bank-note.png';
const billImageReady = new Promise<HTMLImageElement>((resolve) => {
  BILL_IMG.onload = () => resolve(BILL_IMG);
  if (BILL_IMG.complete) resolve(BILL_IMG);
});

// Animation durations (seconds)
const LOCK_FLIP_DURATION = 0.5;
const LID_OPEN_DURATION = 0.8;
// Lock rotation when flipped open (radians) ~115 degrees
const LOCK_OPEN_ANGLE = -(Math.PI / 2 + 25 * Math.PI / 180);
// Lid rotation when fully open
const LID_OPEN_ANGLE = -Math.PI * 0.65;

type SuitcaseState = 'loading' | 'locked' | 'left_unlocked' | 'right_unlocked' | 'unlocked' | 'opening' | 'open';

interface AnimationTask {
  target: THREE.Object3D;
  property: 'x' | 'y' | 'z';
  from: number;
  to: number;
  duration: number;
  elapsed: number;
  ease: (t: number) => number;
  onComplete?: () => void;
}

// Easing functions
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const easeOutBounce = (t: number): number => {
  if (t < 1 / 2.75) {
    return 7.5625 * t * t;
  } else if (t < 2 / 2.75) {
    const t2 = t - 1.5 / 2.75;
    return 7.5625 * t2 * t2 + 0.75;
  } else if (t < 2.5 / 2.75) {
    const t2 = t - 2.25 / 2.75;
    return 7.5625 * t2 * t2 + 0.9375;
  } else {
    const t2 = t - 2.625 / 2.75;
    return 7.5625 * t2 * t2 + 0.984375;
  }
};

export class BonusSuitcaseScene extends EventEmitter {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private shared: SharedRenderer;
  private viewId: string | null = null;
  private clock: THREE.Clock;
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  // Model parts
  private suitcaseGroup: THREE.Group;
  private lid: THREE.Object3D | null = null;
  private lockLeft: THREE.Object3D | null = null;
  private lockRight: THREE.Object3D | null = null;

  // State
  private state: SuitcaseState = 'loading';
  private leftUnlocked = false;
  private rightUnlocked = false;
  private animations: AnimationTask[] = [];
  private container: HTMLElement;
  private boundOnPointerDown: (e: PointerEvent) => void;

  // Prize
  private prizeAmount = 0;
  private shaking = false;

  // Cash layout config
  private cashCols = 4;
  private cashRows = 3;
  private cashLayers = 2;

  // Cash physics
  private physicsWorld: CANNON.World | null = null;
  private cashBodies: { mesh: THREE.Mesh; body: CANNON.Body }[] = [];
  private cashGroup: THREE.Group | null = null;
  private physicsRunning = false;


  public readonly ready: Promise<void>;

  constructor(container: HTMLElement, prizeAmount: number, cashStackCount?: number) {
    super();
    this.container = container;
    this.prizeAmount = prizeAmount;

    // Scale cash stacks based on prize (log-scaled 4-24)
    if (cashStackCount != null) {
      const total = Math.max(4, Math.min(24, cashStackCount));
      // Distribute into grid: cols×rows×layers ≈ total
      // Use 2 layers, solve for cols×rows
      this.cashLayers = total >= 12 ? 2 : 1;
      const perLayer = Math.ceil(total / this.cashLayers);
      this.cashRows = Math.min(3, Math.ceil(perLayer / 4));
      this.cashCols = Math.ceil(perLayer / this.cashRows);
    }
    this.shared = SharedRenderer.instance;
    this.clock = new THREE.Clock();

    // Scene setup
    this.scene = new THREE.Scene();
    this.suitcaseGroup = new THREE.Group();
    this.scene.add(this.suitcaseGroup);

    // Camera — front-on view centered on the suitcase
    this.camera = new THREE.PerspectiveCamera(30, 1, 0.001, 20);
    this.camera.position.set(0, 0.012, 0.09);
    this.camera.lookAt(0, 0.008, 0);

    // Lighting
    this.setupLighting();

    // Input
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    container.addEventListener('pointerdown', this.boundOnPointerDown);

    // Match Blender's brightness level
    this.shared.gl.toneMappingExposure = 1.0;

    // Register view
    console.log('[BonusSuitcaseScene] Container size:', container.clientWidth, 'x', container.clientHeight);
    this.viewId = this.shared.addView({
      scene: this.scene,
      camera: this.camera,
      container,
      priority: 50,
      onBeforeRender: () => this.update(),
    });
    console.log('[BonusSuitcaseScene] View registered:', this.viewId);

    // Load model
    this.ready = this.loadModel();
  }

  private setupLighting(): void {
    // Key light — neutral white from front-right to preserve texture colors
    const keyLight = new THREE.DirectionalLight(0xffffff, 2.0);
    keyLight.position.set(0.04, 0.06, 0.08);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(512, 512);
    this.scene.add(keyLight);

    // Fill light — neutral from left (no blue tint that kills brown)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight.position.set(-0.04, 0.04, 0.02);
    this.scene.add(fillLight);

    // Rim light — subtle warm from behind
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
    rimLight.position.set(0, 0.03, -0.05);
    this.scene.add(rimLight);

    // Ambient — lift shadow areas
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambient);

    // Brown/brass env map — metallic locks reflect this, giving them their brown tint
    const pmrem = new THREE.PMREMGenerator(this.shared.gl);
    pmrem.compileCubemapShader();
    const envScene = new THREE.Scene();
    envScene.background = new THREE.Color(0x2a1a0a); // Dark brown base
    // Brass-colored top light
    const envTop = new THREE.PointLight(0xc89850, 4, 20);
    envTop.position.set(0, 5, 0);
    envScene.add(envTop);
    // Warm brown front
    const envFront = new THREE.PointLight(0xb08040, 3, 20);
    envFront.position.set(0, 2, 5);
    envScene.add(envFront);
    // Softer brown from below
    const envBottom = new THREE.PointLight(0x806030, 2, 20);
    envBottom.position.set(0, -3, 0);
    envScene.add(envBottom);
    const envMap = pmrem.fromScene(envScene, 0.04).texture;
    pmrem.dispose();
    this.scene.environment = envMap;
  }

  private async loadModel(): Promise<void> {
    const loader = new GLTFLoader();

    try {
      const gltf = await loader.loadAsync(SUITCASE_GLB);
      const model = gltf.scene;

      // Enable shadows — preserve original texture colors
      model.traverse((node) => {
        if ((node as THREE.Mesh).isMesh) {
          const mesh = node as THREE.Mesh;
          mesh.castShadow = true;
          mesh.receiveShadow = true;

          const mat = mesh.material as THREE.MeshStandardMaterial;
          if (mat.isMeshStandardMaterial) {
            mat.envMapIntensity = 0.5;
          }
        }
      });

      // Find named parts
      this.lid = model.getObjectByName('lid') ?? null;
      this.lockLeft = model.getObjectByName('lock_left') ?? null;
      this.lockRight = model.getObjectByName('lock_right') ?? null;

      // Add invisible hit zones to locks for easier tapping
      const hitMat = new THREE.MeshBasicMaterial({ visible: false });
      if (this.lockLeft) {
        const box = new THREE.Box3().setFromObject(this.lockLeft);
        const size = box.getSize(new THREE.Vector3());
        const hitGeo = new THREE.BoxGeometry(size.x * 3, size.y * 3, size.z * 3);
        const hitMesh = new THREE.Mesh(hitGeo, hitMat);
        this.lockLeft.add(hitMesh);
      }
      if (this.lockRight) {
        const box = new THREE.Box3().setFromObject(this.lockRight);
        const size = box.getSize(new THREE.Vector3());
        const hitGeo = new THREE.BoxGeometry(size.x * 3, size.y * 3, size.z * 3);
        const hitMesh = new THREE.Mesh(hitGeo, hitMat);
        this.lockRight.add(hitMesh);
      }

      // Rotate so locks face the camera
      model.rotation.y = -Math.PI / 2;

      this.suitcaseGroup.add(model);

      // Center the model
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);
      // Place on "ground" (shift up by half height)
      const halfH = (box.max.y - box.min.y) / 2;
      model.position.y += halfH;

      // Create cash stacks inside the base
      this.createCashStacks(model);

      this.state = 'locked';
      this.emit('state', { state: this.state });
    } catch (err) {
      console.error('[BonusSuitcaseScene] Failed to load model:', err);
    }
  }

  private loadBillTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;

    const draw = (img: HTMLImageElement) => {
      // Draw rotated 90°: swap width/height
      canvas.width = img.height;
      canvas.height = img.width;
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();

      // Yellow/gold band across the middle (horizontal)
      const bandH = canvas.height * 0.22;
      const bandY = (canvas.height - bandH) / 2;
      ctx.fillStyle = '#d4a830';
      ctx.fillRect(0, bandY, canvas.width, bandH);
      ctx.fillStyle = '#c09828';
      ctx.fillRect(0, bandY + 3, canvas.width, bandH - 6);

      // Band text
      ctx.fillStyle = '#8a7020';
      const fs = Math.max(8, Math.round(bandH * 0.4));
      ctx.font = `bold ${fs}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('$10,000', canvas.width / 2, canvas.height / 2);

      tex.needsUpdate = true;
    };

    // Use preloaded image — draw immediately if ready, otherwise wait
    if (BILL_IMG.complete && BILL_IMG.naturalWidth > 0) {
      draw(BILL_IMG);
    } else {
      billImageReady.then(draw);
    }

    return tex;
  }

  /** Long side texture: green with stacked bill lines + yellow band in the middle */
  private createCashLongSideTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Alternating green stripes — high contrast for stacked bill look
    for (let y = 0; y < 64; y += 4) {
      ctx.fillStyle = y % 8 < 4 ? '#45914f' : '#275e2e';
      ctx.fillRect(0, y, 128, 4);
    }

    // Yellow/gold band across the middle
    ctx.fillStyle = '#d4a830';
    ctx.fillRect(0, 24, 128, 16);
    ctx.fillStyle = '#c09828';
    ctx.fillRect(0, 26, 128, 12);

    // Band stacking lines
    for (let y = 24; y < 40; y += 4) {
      ctx.fillStyle = y % 8 < 4 ? '#c49828' : '#9a7818';
      ctx.fillRect(0, y, 128, 2);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /** Short side texture: paper/cream with stacked bill lines, no band */
  private createCashShortSideTexture(): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    // Alternating cream/grey stripes — high contrast for stacked paper edges
    for (let y = 0; y < 64; y += 4) {
      ctx.fillStyle = y % 8 < 4 ? '#f0ece0' : '#c0bab0';
      ctx.fillRect(0, y, 64, 4);
    }

    // Yellow/gold band — vertical, continuing from top face
    ctx.fillStyle = '#d4a830';
    ctx.fillRect(24, 0, 16, 64);
    ctx.fillStyle = '#c09828';
    ctx.fillRect(26, 0, 12, 64);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  private createCashStacks(model: THREE.Object3D): void {
    const base = model.getObjectByName('base');
    if (!base) return;

    const baseBox = new THREE.Box3().setFromObject(base);
    const baseSize = baseBox.getSize(new THREE.Vector3());
    const baseCenter = baseBox.getCenter(new THREE.Vector3());

    // Stack dimensions — half width, triple height (taller stacks)
    const stackW = baseSize.x * 0.14;
    const stackD = baseSize.z * 0.40;
    const stackH = baseSize.y * 0.90;

    const billTex = this.loadBillTexture();
    const longSideTex = this.createCashLongSideTexture();
    const shortSideTex = this.createCashShortSideTexture();

    // Materials: top/bottom = bill face, long sides = green+band+lines, short sides = paper+lines
    const cashTopMat = new THREE.MeshStandardMaterial({ map: billTex, roughness: 0.8, metalness: 0 });
    const cashSideMat = new THREE.MeshStandardMaterial({ map: longSideTex, roughness: 0.9, metalness: 0 });
    const cashEdgeMat = new THREE.MeshStandardMaterial({ map: shortSideTex, roughness: 0.95, metalness: 0 });
    // Box faces order: +x, -x, +y, -y, +z, -z
    const materials = [cashEdgeMat, cashEdgeMat, cashTopMat, cashTopMat, cashSideMat, cashSideMat];

    this.cashGroup = new THREE.Group();
    const geo = new THREE.BoxGeometry(stackW, stackH, stackD);

    // Setup physics world (scaled: 1 unit in physics = 1 unit in Three.js, model space)
    this.physicsWorld = new CANNON.World({
      gravity: new CANNON.Vec3(0, -0.5, 0), // Gentle for tiny model scale
    });
    this.physicsWorld.defaultContactMaterial.friction = 0.8;
    this.physicsWorld.defaultContactMaterial.restitution = 0.0;

    // Suitcase base floor — static plane
    const floorY = baseCenter.y - baseSize.y * 0.4;
    // Floor: group 1, collides with all layer groups (2, 4, 8, ...)
    const allLayerMask = Array.from({ length: this.cashLayers }, (_, i) => 1 << (i + 1)).reduce((a, b) => a | b, 0);
    const floorBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: new CANNON.Plane(),
      collisionFilterGroup: 1,
      collisionFilterMask: allLayerMask,
    });
    floorBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0); // Face up
    floorBody.position.set(baseCenter.x, floorY, baseCenter.z);
    this.physicsWorld.addBody(floorBody);

    // No side walls — stacks stay in place with high damping and gentle spread

    // Create cash stacks using configured layout
    const cols = this.cashCols;
    const rows = this.cashRows;
    const layers = this.cashLayers;
    const gapX = stackW * 0.08;
    const gapZ = stackD * 0.06;
    const totalW = cols * stackW + (cols - 1) * gapX;
    const totalD = rows * stackD + (rows - 1) * gapZ;

    const halfExtents = new CANNON.Vec3(stackW / 2, stackH / 2, stackD / 2);

    for (let layer = 0; layer < layers; layer++) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const mesh = new THREE.Mesh(geo, materials);
          mesh.castShadow = true;

          const x = -totalW / 2 + stackW / 2 + c * (stackW + gapX);
          const z = -totalD / 2 + stackD / 2 + r * (stackD + gapZ);
          const y = floorY + stackH / 2 + layer * stackH;

          // Slight offset per layer for natural look
          const layerOffX = layer * stackW * 0.05 * (Math.random() - 0.5);
          const layerOffZ = layer * stackD * 0.05 * (Math.random() - 0.5);

          const posX = baseCenter.x + x + layerOffX;
          const posY = y;
          const posZ = baseCenter.z + z + layerOffZ;

          mesh.position.set(posX, posY, posZ);
          mesh.rotation.y = (Math.random() - 0.5) * 0.05;

          // Per-layer collision: each layer is its own group (2, 4, 8, ...),
          // collides with floor (1) + other layers, but NOT same-layer stacks
          const layerGroup = 1 << (layer + 1); // layer 0 = group 2, layer 1 = group 4, etc.
          const otherLayersMask = allLayerMask ^ layerGroup; // All layers except own
          const body = new CANNON.Body({
            mass: 0,
            shape: new CANNON.Box(halfExtents),
            position: new CANNON.Vec3(posX, posY, posZ),
            linearDamping: 0.15,
            angularDamping: 0.95,
            collisionFilterGroup: layerGroup,
            collisionFilterMask: 1 | otherLayersMask, // Floor + other layers
          });
          body.quaternion.setFromEuler(0, mesh.rotation.y, 0);

          this.physicsWorld.addBody(body);
          this.cashGroup.add(mesh);
          this.cashBodies.push({ mesh, body });
        }
      }
    }

    model.add(this.cashGroup);
  }

  private activateCashPhysics(): void {
    if (!this.physicsWorld || this.cashBodies.length === 0) return;

    // Lift relative to suitcase height — needs to be visible above the rim
    const sampleMesh = this.cashBodies[0].mesh;
    const geo = sampleMesh.geometry as THREE.BoxGeometry;
    const stackH = geo.parameters.height;
    // Lift half of 0.015m
    const liftHeight = 0.0075;

    // Pre-compute per-stack lift parameters — higher layers lift more
    const perLayer = this.cashCols * this.cashRows;
    const liftParams = this.cashBodies.map((_, i) => {
      const layer = Math.floor(i / perLayer);
      // Bottom layer: 0.5–0.75x, top layers: 1.0–1.5x (scales with layer index)
      const layerBase = 0.5 + (layer / Math.max(this.cashLayers - 1, 1)) * 0.5;
      const randomMultiplier = layerBase + Math.random() * 0.5;
      return {
        lift: liftHeight * randomMultiplier,
        spreadX: (Math.random() - 0.5) * 0.001,
        spreadZ: (Math.random() - 0.5) * 0.001,
      };
    });

    // Store start positions
    const startPositions = this.cashBodies.map(({ body }) => ({
      x: body.position.x,
      y: body.position.y,
      z: body.position.z,
    }));

    console.log('[Cash] activateCashPhysics: stacks:', this.cashBodies.length, 'stackH:', stackH, 'liftHeight:', liftHeight);
    console.log('[Cash] start positions:', startPositions.map(p => `(${p.x.toFixed(4)}, ${p.y.toFixed(4)}, ${p.z.toFixed(4)})`));

    // Phase 1: Gently lift upward over 0.3s
    const liftDuration = 0.3;
    let liftElapsed = 0;

    const liftInterval = setInterval(() => {
      liftElapsed += 0.016;
      const t = Math.min(liftElapsed / liftDuration, 1);
      const eased = easeOutCubic(t);

      this.cashBodies.forEach(({ mesh, body }, i) => {
        const start = startPositions[i];
        const p = liftParams[i];
        body.position.set(
          start.x + p.spreadX * eased,
          start.y + p.lift * eased,
          start.z + p.spreadZ * eased,
        );
        mesh.position.set(body.position.x, body.position.y, body.position.z);
      });

      if (t >= 1) {
        clearInterval(liftInterval);
        // Phase 2: All layers become dynamic and fall with physics
        this.physicsRunning = true;
        this.physicsStepCount = 0;
        this.cashBodies.forEach(({ body }) => {
          body.mass = 0.01;
          body.updateMassProperties();
          body.type = CANNON.Body.DYNAMIC;
          body.velocity.set(0, 0, 0);
          body.angularVelocity.set(
            (Math.random() - 0.5) * 0.3,
            (Math.random() - 0.5) * 1.5,
            (Math.random() - 0.5) * 0.3,
          );
          body.wakeUp();
        });
      }
    }, 16);
  }

  private physicsStepCount = 0;

  private stepPhysics(dt: number): void {
    if (!this.physicsRunning || !this.physicsWorld) return;

    this.physicsWorld.step(1 / 60, dt, 3);
    this.physicsStepCount++;

    // Sync all meshes
    for (const { mesh, body } of this.cashBodies) {
      if (body.type === CANNON.Body.STATIC && body.mass === 0) continue;
      mesh.position.set(body.position.x, body.position.y, body.position.z);
      mesh.quaternion.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w);
    }

    // After enough time for all layers to fall and land, check if ALL bodies are slow
    if (this.physicsStepCount > 30) {
      const allSlow = this.cashBodies.every(({ body }) =>
        (body.type === CANNON.Body.STATIC && body.mass === 0) ||
        (Math.abs(body.velocity.y) < 0.005 && body.velocity.length() < 0.01)
      );
      if (allSlow) {
        for (const { body } of this.cashBodies) {
          body.type = CANNON.Body.STATIC;
          body.mass = 0;
          body.velocity.set(0, 0, 0);
          body.angularVelocity.set(0, 0, 0);
        }
      }
    }

    // Stop physics entirely when all frozen
    const allFrozen = this.cashBodies.every(({ body }) => body.type === CANNON.Body.STATIC && body.mass === 0);
    if (allFrozen) {
      this.physicsRunning = false;
    }
  }

  private update(): void {
    const dt = this.clock.getDelta();

    // Step cash physics
    this.stepPhysics(dt);

    // Process animations
    for (let i = this.animations.length - 1; i >= 0; i--) {
      const anim = this.animations[i];
      anim.elapsed += dt;
      const t = Math.min(anim.elapsed / anim.duration, 1);
      const eased = anim.ease(t);
      const value = anim.from + (anim.to - anim.from) * eased;
      anim.target.rotation[anim.property] = value;

      if (t >= 1) {
        anim.onComplete?.();
        this.animations.splice(i, 1);
      }
    }

    // Subtle idle sway only before opening
    if (this.state === 'locked' || this.state === 'left_unlocked' || this.state === 'right_unlocked' || this.state === 'unlocked') {
      const elapsed = this.clock.elapsedTime;
      this.suitcaseGroup.rotation.y = Math.sin(elapsed * 0.5) * 0.03;
    } else {
      // Reset rotation when opening/open so no tilt
      this.suitcaseGroup.rotation.set(0, 0, 0);
    }
  }

  private onPointerDown(e: PointerEvent): void {
    if (this.state === 'loading' || this.state === 'opening') return;

    const rect = this.container.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);

    // Check if tapping a lock first
    if (!this.leftUnlocked && this.lockLeft) {
      const hits = this.raycaster.intersectObject(this.lockLeft, true);
      if (hits.length > 0) {
        this.unlockLeft();
        return;
      }
    }
    if (!this.rightUnlocked && this.lockRight) {
      const hits = this.raycaster.intersectObject(this.lockRight, true);
      if (hits.length > 0) {
        this.unlockRight();
        return;
      }
    }

    // Check if tapping suitcase body/lid
    const bodyHits = this.raycaster.intersectObject(this.suitcaseGroup, true);
    if (bodyHits.length > 0) {
      if (this.state === 'unlocked') {
        this.openLid();
      } else if (this.state === 'locked' || this.state === 'left_unlocked' || this.state === 'right_unlocked') {
        // Tried to open with locks still closed — reject
        this.rejectOpen();
      } else if (this.state === 'open') {
        this.emit('claim', { amount: this.prizeAmount });
      }
    }
  }

  private unlockLeft(): void {
    if (!this.lockLeft || this.leftUnlocked) return;
    this.leftUnlocked = true;
    this.checkBothUnlocked(); // Update state immediately, don't wait for animation

    this.animations.push({
      target: this.lockLeft,
      property: 'z',
      from: this.lockLeft.rotation.z,
      to: this.lockLeft.rotation.z + LOCK_OPEN_ANGLE,
      duration: LOCK_FLIP_DURATION,
      elapsed: 0,
      ease: easeOutBounce,
    });

    sound.play(SoundId.CASE_UNLOCK);
    this.emit('lock:open', { side: 'left' });
  }

  private unlockRight(): void {
    if (!this.lockRight || this.rightUnlocked) return;
    this.rightUnlocked = true;
    this.checkBothUnlocked(); // Update state immediately, don't wait for animation

    this.animations.push({
      target: this.lockRight,
      property: 'z',
      from: this.lockRight.rotation.z,
      to: this.lockRight.rotation.z + LOCK_OPEN_ANGLE,
      duration: LOCK_FLIP_DURATION,
      elapsed: 0,
      ease: easeOutBounce,
    });

    sound.play(SoundId.CASE_UNLOCK);
    this.emit('lock:open', { side: 'right' });
  }

  private checkBothUnlocked(): void {
    if (this.leftUnlocked && this.rightUnlocked) {
      this.state = 'unlocked';
      this.emit('state', { state: 'unlocked' });
    } else if (this.leftUnlocked) {
      this.state = 'left_unlocked';
      this.emit('state', { state: 'left_unlocked' });
    } else if (this.rightUnlocked) {
      this.state = 'right_unlocked';
      this.emit('state', { state: 'right_unlocked' });
    }
  }

  private rejectOpen(): void {
    if (this.shaking) return;
    this.shaking = true;
    sound.play(SoundId.CASE_LOCKED);
    this.emit('reject', {});

    // Shake the suitcase on Y and X rotation
    const duration = 0.4;
    const shakeFreq = 25;
    const shakeAmplitude = 0.06;
    let elapsed = 0;
    const baseY = this.suitcaseGroup.rotation.y;
    const baseX = this.suitcaseGroup.rotation.x;

    const interval = setInterval(() => {
      elapsed += 0.016;
      const t = Math.min(elapsed / duration, 1);
      const decay = 1 - t;
      const shake = Math.sin(elapsed * shakeFreq) * shakeAmplitude * decay;
      this.suitcaseGroup.rotation.y = baseY + shake;
      this.suitcaseGroup.rotation.x = baseX + shake * 0.5;

      if (t >= 1) {
        this.suitcaseGroup.rotation.y = baseY;
        this.suitcaseGroup.rotation.x = baseX;
        this.shaking = false;
        clearInterval(interval);
      }
    }, 16);
  }

  private openLid(): void {
    if (!this.lid || this.state !== 'unlocked') return;
    this.state = 'opening';
    this.emit('state', { state: 'opening' });

    // Camera rotates into view over full duration.
    // Halfway through, lid starts opening + cash/godrays/prize fire.
    const lidRotFrom = this.lid.rotation.z;
    const lidRotTo = this.lid.rotation.z - LID_OPEN_ANGLE;
    let lidStarted = false;

    this.animateCamera(
      // onMidpoint — fires at 50% of camera travel, then tiny pause before lid pops
      () => {
        if (lidStarted) return;
        lidStarted = true;

        // Brief pause to emulate tough/stuck opening before it pops
        setTimeout(() => {
          this.animations.push({
            target: this.lid!,
            property: 'z',
            from: lidRotFrom,
            to: lidRotTo,
            duration: LID_OPEN_DURATION,
            elapsed: 0,
            ease: easeOutCubic,
            onComplete: () => {
              this.state = 'open';
              this.emit('state', { state: 'open' });
            },
          });

          // Lid pops open now; the prize stinger follows a beat later, picked
          // by tier (≥ 20M = the big reveal).
          sound.play(SoundId.CASE_OPEN);
          const prizeSound = this.prizeAmount >= BIG_PRIZE_THRESHOLD
            ? SoundId.CASE_PRIZE_BIG
            : SoundId.CASE_PRIZE_SMALL;
          setTimeout(() => sound.play(prizeSound), PRIZE_SFX_DELAY_MS);

          this.emit('godrays', {});
          this.activateCashPhysics();
          this.emit('prize:revealed', { amount: this.prizeAmount });
        }, 250);
      },
    );
  }

  private animateCamera(onMidpoint?: () => void): void {
    const startPos = this.camera.position.clone();
    const targetPos = new THREE.Vector3(0, 0.066, 0.061);
    const startLook = new THREE.Vector3(0, 0.008, 0);
    const targetLook = new THREE.Vector3(0, 0.012, -0.005);
    const halfDuration = 0.4; // Each half of the camera move
    const pauseDuration = 250; // ms — tough opening pause
    let elapsed = 0;

    // Phase 1: Camera moves to midpoint
    const phase1 = setInterval(() => {
      elapsed += 0.016;
      const t = Math.min(elapsed / halfDuration, 1);
      const e = easeOutCubic(t);
      // Only interpolate first half (0 → 0.5 of total travel)
      this.camera.position.lerpVectors(startPos, targetPos, e * 0.5);
      const look = new THREE.Vector3().lerpVectors(startLook, targetLook, e * 0.5);
      this.camera.lookAt(look);

      if (t >= 1) {
        clearInterval(phase1);
        onMidpoint?.();

        // Phase 2: After pause, camera continues to final position
        setTimeout(() => {
          const midPos = this.camera.position.clone();
          const midLook = new THREE.Vector3().lerpVectors(startLook, targetLook, 0.5);
          let elapsed2 = 0;

          const phase2 = setInterval(() => {
            elapsed2 += 0.016;
            const t2 = Math.min(elapsed2 / halfDuration, 1);
            const e2 = easeOutCubic(t2);
            this.camera.position.lerpVectors(midPos, targetPos, e2);
            const look2 = new THREE.Vector3().lerpVectors(midLook, targetLook, e2);
            this.camera.lookAt(look2);

            if (t2 >= 1) {
              clearInterval(phase2);
            }
          }, 16);
        }, pauseDuration);
      }
    }, 16);
  }

  /** Get current interactive state. */
  getState(): SuitcaseState {
    return this.state;
  }

  dispose(): void {
    // Restore default exposure
    this.shared.gl.toneMappingExposure = 1.0;

    // Remove input listener
    this.container.removeEventListener('pointerdown', this.boundOnPointerDown);

    // Remove view
    if (this.viewId) {
      this.shared.removeView(this.viewId);
      this.viewId = null;
    }

    // Dispose scene resources
    this.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material?.dispose();
        }
      }
    });

    this.scene.environment?.dispose();
    this.removeAllListeners();
  }
}
